export type UnityLoadPhase =
  | "idle"
  | "fetching"
  | "booting"
  | "running"
  | "quitting"
  | "aborted"
  | "failed";

export type UnityLoadOutcome = "loaded" | "aborted" | "failed";

export type UnityLoadAbortDecision = {
  outcomeEmitted: "aborted" | null;
  abortPhase: UnityLoadPhase | null;
  shouldAwaitBoot: boolean;
  shouldQuit: boolean;
};

export type UnityLoadAttempt = {
  readonly phase: UnityLoadPhase;
  readonly outcome: UnityLoadOutcome | null;
  readonly signal: AbortSignal;
  elapsedMs: () => number;
  isAborted: () => boolean;
  isTerminal: () => boolean;
  start: () => void;
  enterBooting: () => void;
  markLoaded: () => boolean;
  fail: () => boolean;
  abort: () => UnityLoadAbortDecision;
};

const NO_ABORT_DECISION: UnityLoadAbortDecision = {
  outcomeEmitted: null,
  abortPhase: null,
  shouldAwaitBoot: false,
  shouldQuit: false,
};

function defaultNow(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

/**
 * One Unity load attempt. Cancellation is a first-class outcome so unmount /
 * navigation during boot is not reported as a load failure.
 */
export function createUnityLoadAttempt(options?: { now?: () => number }): UnityLoadAttempt {
  const now = options?.now ?? defaultNow;
  const controller = new AbortController();
  let phase: UnityLoadPhase = "idle";
  let outcome: UnityLoadOutcome | null = null;
  let startedAtMs: number | null = null;
  let failureReported = false;

  const attempt: UnityLoadAttempt = {
    get phase() {
      return phase;
    },
    get outcome() {
      return outcome;
    },
    get signal() {
      return controller.signal;
    },
    elapsedMs() {
      if (startedAtMs === null) {
        return 0;
      }
      return Math.max(0, Math.round(now() - startedAtMs));
    },
    isAborted() {
      return phase === "aborted";
    },
    isTerminal() {
      return phase === "aborted" || phase === "failed" || phase === "quitting";
    },
    start() {
      if (phase !== "idle") {
        return;
      }
      startedAtMs = now();
      phase = "fetching";
    },
    enterBooting() {
      if (phase !== "fetching") {
        return;
      }
      phase = "booting";
    },
    markLoaded() {
      if (phase !== "booting" && phase !== "fetching") {
        return false;
      }
      phase = "running";
      outcome = "loaded";
      return true;
    },
    fail() {
      if (phase === "aborted" || phase === "failed" || phase === "quitting") {
        return false;
      }
      if (failureReported) {
        return false;
      }
      failureReported = true;
      if (phase !== "running") {
        phase = "failed";
        outcome = "failed";
      }
      return true;
    },
    abort() {
      if (phase === "idle" || phase === "aborted" || phase === "failed" || phase === "quitting") {
        return NO_ABORT_DECISION;
      }

      const from = phase;
      if (!controller.signal.aborted) {
        controller.abort();
      }

      if (from === "running") {
        phase = "quitting";
        return {
          outcomeEmitted: null,
          abortPhase: null,
          shouldAwaitBoot: false,
          shouldQuit: true,
        };
      }

      phase = "aborted";
      outcome = "aborted";
      return {
        outcomeEmitted: "aborted",
        abortPhase: from,
        shouldAwaitBoot: from === "booting",
        shouldQuit: false,
      };
    },
  };

  return attempt;
}
